import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
	Content,
	ContentCropper,
	ContentDatabaseModifier,
	ContentOrderFields,
	ContentOwnerFilter,
	ContentSearchModifier,
	ContentVisibility,
	CropperData,
	OrderDirection,
	SearchAutocompleteResult,
	SharePointPermission,
	User,
} from '@efficiently/models';

import { ContentSearchFilters, ContentsRepository } from '@efficiently/client-api-content';
import { GraphQLClient, GraphqlExecutorService } from '@efficiently/client-core-graphql';
import type { ContentSearchArgs, CropperContentUploadArgs } from '@efficiently/client-core-graphql-models';
import { Pagination } from '@efficiently/client-util-classes';
import { getBrowserTimeZone } from '@efficiently/client-util-date-time';

import {
	ARCHIVE_CONTENTS,
	DELETE_CONTENTS,
	GET_CONTENT_AUTOCOMPLETE_ACTIONS,
	GET_CONTENT_CROPPER_BY_URL,
	GET_CONTENTS,
	GET_DOWNLOAD_CONTENT_URL,
	PROJECT_DOCUMENT_CREATORS,
	REMOVE_BACKGROUND,
	TOGGLE_PUBLISH_STATUS,
	UPDATE_CONTENT,
	UPLOAD_CONTENTS,
	UPLOAD_CROPPER_CONTENT,
} from '../graphql/queries';

interface ImageContentData {
	content: Content;
	cropper: ContentCropper;
}

// TEMP-CODEGEN-FRONTIER(EFF-24232): the content operations are typed against the generated schema, but the
// abstract repo still exposes the domain `Content` / `ContentCropper` / `SearchAutocompleteResult` / `User`
// shapes consumed app-wide (including still-un-migrated forbidden clusters). Every `as unknown as` in this file
// is that generated↔domain boundary and MUST stay confined to it — a cast that is not a frontier cast does not
// belong here. Remove them as the content consumers migrate. This repo-edge frontier is the sanctioned
// exception to the cast-free rule; see docs/common/graphql-codegen-development.md.
@Injectable()
export class ContentsGqlRepository extends ContentsRepository {
	private readonly gql = inject(GraphqlExecutorService);

	// TODO EFF-13647 - add error handling
	public override getContents(
		search: string,
		projectId: string,
		bookId: string,
		folderIds: string[],
		folderParent: string,
		pagination: Pagination,
		mode: ContentSearchModifier = ContentSearchModifier.STANDARD_MODE,
		ownerFilter: ContentOwnerFilter = ContentOwnerFilter.ALL,
		order: ContentOrderFields = ContentOrderFields.createdDate,
		direction: OrderDirection = OrderDirection.DESC,
		tags: string[] = null,
		filters: ContentSearchFilters = null,
	): Observable<{ contents: Content[]; pagination: Pagination }> {
		return this.gql.watchOnce(GET_CONTENTS, {
			variables: {
				args: {
					search,
					projectId,
					bookId,
					folderIds,
					folderParent,
					pagination,
					mode,
					ownerFilter,
					timeZoneCode: getBrowserTimeZone(),
					order,
					direction,
					tags,
					filters,
				} as unknown as ContentSearchArgs,
			},
			client: GraphQLClient.Core,
			select: data => data.contents as unknown as { contents: Content[]; pagination: Pagination },
		});
	}

	public override getContentCropperByUrl(url: string): Observable<ImageContentData> {
		return this.gql.watchOnce(GET_CONTENT_CROPPER_BY_URL, {
			variables: { url },
			client: GraphQLClient.Core,
			select: data => data.contentCropperByUrl as unknown as ImageContentData,
		});
	}

	// TODO EFF-13647 - add error handling
	public override uploadContents(
		files: File[],
		projectId?: string,
		projectOrgId?: string,
		projectOrgCode?: string,
		folderId?: string,
		folderParent?: string,
		parentId?: string,
		parentType?: string,
		documentType?: string,
		visibility: ContentVisibility = ContentVisibility.Private,
		modifier: ContentDatabaseModifier = ContentDatabaseModifier.STANDARD_MODE,
	): Observable<Content[]> {
		return this.gql.mutate(UPLOAD_CONTENTS, {
			variables: {
				args: {
					files,
					projectId,
					projectOrgId,
					projectOrgCode,
					folderId,
					folderParent,
					parentId,
					parentType,
					documentType,
					visibility,
					modifier,
				},
			},
			client: GraphQLClient.Core,
			select: data => data.uploadContents as unknown as Content[],
		});
	}

	// TODO EFF-13647 - add error handling
	public override uploadCropperContent(
		contentId: string,
		file: File,
		cropperData: CropperData,
		applyAsDefaultContent: boolean,
	): Observable<{ content: Content; cropper: ContentCropper }> {
		return this.gql.mutate(UPLOAD_CROPPER_CONTENT, {
			variables: {
				args: { contentId, file, cropperData, applyAsDefaultContent } as unknown as CropperContentUploadArgs,
			},
			client: GraphQLClient.Core,
			select: data => data.uploadCropperContent as unknown as { content: Content; cropper: ContentCropper },
		});
	}

	public override rename(id: string, fileName: string): Observable<Content> {
		return this.gql.mutate(UPDATE_CONTENT, {
			variables: { args: { id, fileName } },
			client: GraphQLClient.Core,
			select: data => data.updateContent as unknown as Content,
		});
	}

	public override archiveContents(ids: string[]): Observable<string[]> {
		return this.gql.mutate(ARCHIVE_CONTENTS, {
			variables: { ids },
			client: GraphQLClient.Core,
			select: data => data.archiveContents ?? [],
		});
	}

	public override deleteContents(ids: string[]): Observable<string[]> {
		return this.gql.mutate(DELETE_CONTENTS, {
			variables: { ids },
			client: GraphQLClient.Core,
			select: data => data.deleteContents ?? [],
		});
	}

	public override getDownloadContentUrl(contentId: string, inline = false): Observable<string | null> {
		return this.gql.mutate(GET_DOWNLOAD_CONTENT_URL, {
			variables: { args: { id: contentId, inline } },
			client: GraphQLClient.Core,
			select: data => data.getDownloadContentUrl ?? null,
		});
	}

	public override getContentAutocompleteValues(args: {
		projectId: string;
		search: string;
	}): Observable<SearchAutocompleteResult[]> {
		return this.gql.watchOnce(GET_CONTENT_AUTOCOMPLETE_ACTIONS, {
			variables: {
				...args,
				mode: ContentSearchModifier.DOCUMENTS_MODE,
				timeZoneCode: getBrowserTimeZone(),
				ownerFilter: ContentOwnerFilter.ALL,
			},
			client: GraphQLClient.Core,
			select: data => data.contentAutocompleteValues as unknown as SearchAutocompleteResult[],
		});
	}

	public override toggleContentPublishStatus(
		contentId: string,
		permission: SharePointPermission,
	): Observable<string> {
		return this.gql.mutate(TOGGLE_PUBLISH_STATUS, {
			variables: { contentId, permission },
			client: GraphQLClient.Core,
			select: data => data.sharePoint ?? '',
		});
	}

	/**
	 * Removes the image background through the Core GraphQL mutation.
	 *
	 * This request skips the global error handler, so every call site must handle
	 * errors locally, including user feedback and UI state recovery.
	 */
	public override removeBackground(file: File, contentId?: string): Observable<{ base64img: string }> {
		return this.gql.mutate(REMOVE_BACKGROUND, {
			variables: { file, contentId },
			client: GraphQLClient.Core,
			// Errors are handled at the call site (image-editor-dialog) with a custom
			// snackbar + retry, so we skip the global handler to avoid a duplicate toast.
			skipGlobalErrorHandler: true,
			select: data => data.removeBackground as unknown as { base64img: string },
		});
	}

	public override getProjectDocumentCreators(projectId: string): Observable<User[]> {
		return this.gql.watchOnce(PROJECT_DOCUMENT_CREATORS, {
			variables: { projectId },
			client: GraphQLClient.Core,
			select: data => data.projectDocumentCreators as unknown as User[],
		});
	}
}
